import { useState } from "react";
import { Bell, Mail, MessageCircle, Pencil, Truck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fdt, inr, useApi, waPhone } from "@/lib/api";

const METHODS = [["AGENT", "Delivery Agent"], ["GN_DELIVERY", "GN Delivery"], ["CUSTOMER_PICKUP", "Customer Pickup"]];

export const trackingInfo = (d) => {
  if (!d) return "";
  if (d.deliveryType === "AGENT") return `Courier: ${d.deliveryPartnerName}, Tracking: ${d.trackingId}`;
  if (d.deliveryType === "CUSTOMER_PICKUP") return `Pickup scheduled: ${fdt(d.pickupAt)}`;
  return `Delivered by ${d.deliveredByName}${d.deliveryPersonPhone ? ` (${d.deliveryPersonPhone})` : ""}`;
};

export function DeliveryForm({ project, onSaved, disabled }) {
  const [f, setF] = useState({ deliveryType: "AGENT", deliveryPartnerName: "", trackingId: "", deliveredByName: "", deliveryPersonPhone: "", pickupAt: "" });
  const [editing, setEditing] = useState(false);
  if (project.delivery && !editing) {
    const d = project.delivery;
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm" data-testid="delivery-record">
        <div className="flex items-center gap-2 font-medium"><Truck className="h-4 w-4" />{(METHODS.find((m) => m[0] === d.deliveryType) || [0, "Self delivery"])[1]}</div>
        <div className="text-muted-foreground mt-1">{trackingInfo(d)} · recorded {fdt(d.deliveredAt)}</div>
        {!disabled && <Button size="sm" variant="ghost" className="mt-2 h-7 px-2" onClick={() => { setF({ ...f, ...d }); setEditing(true); }} data-testid="edit-delivery-button"><Pencil className="h-3.5 w-3.5 mr-1" />Edit delivery</Button>}
      </div>
    );
  }
  const save = async () => {
    try { onSaved((await api.post(`/projects/${project.id}/delivery`, f)).data); setEditing(false); toast.success("Delivery recorded"); } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = (k, l, type = "text", ph = "") => (
    <div className="space-y-1.5"><Label>{l}</Label><Input type={type} value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`delivery-${k}-input`} /></div>
  );
  return (
    <div className="rounded-lg border p-4 space-y-4" data-testid="delivery-form">
      <div className="text-sm font-medium">Delivery method</div>
      <RadioGroup value={f.deliveryType} onValueChange={(v) => setF({ ...f, deliveryType: v })} className="flex flex-wrap gap-x-6 gap-y-2">
        {METHODS.map(([v, l]) => <label key={v} className="flex items-center gap-2 text-sm"><RadioGroupItem value={v} data-testid={`delivery-type-${v.toLowerCase()}`} />{l}</label>)}
      </RadioGroup>
      <div className="grid sm:grid-cols-2 gap-3">
        {f.deliveryType === "AGENT" && <>{inp("deliveryPartnerName", "Delivery partner", "text", "Bluedart, Delhivery…")}{inp("trackingId", "Tracking consignment number")}</>}
        {f.deliveryType === "GN_DELIVERY" && <>{inp("deliveredByName", "Delivery person's name")}{inp("deliveryPersonPhone", "Delivery person's contact", "tel")}</>}
        {f.deliveryType === "CUSTOMER_PICKUP" && inp("pickupAt", "Pickup date & time slot", "datetime-local")}
      </div>
      <Button size="sm" variant="outline" onClick={save} disabled={disabled} data-testid="save-delivery-button">Save delivery details</Button>
    </div>
  );
}

export function ProjectEditDialog({ project, onSaved }) {
  const [open, setOpen] = useState(false);
  const init = () => ({ customerName: project.customerName, customerPhone: project.customerPhone, customerEmail: project.customerEmail || "",
    customerAddress: project.customerAddress, advancePaidAmount: project.advancePaidAmount, paymentRefNumber: project.paymentRefNumber, customerVisitAt: project.customerVisitAt || "" });
  const [f, setF] = useState(init);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { onSaved((await api.patch(`/projects/${project.id}`, { ...f, advancePaidAmount: +f.advancePaidAmount })).data); toast.success("Project updated"); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => { setF(init()); setOpen(true); }} data-testid="edit-project-button"><Pencil className="h-4 w-4 mr-2" />Edit</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="edit-project-dialog">
          <DialogHeader><DialogTitle>Edit {project.projectCode}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Customer name</Label><Input value={f.customerName} onChange={set("customerName")} data-testid="edit-project-name-input" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={f.customerPhone} onChange={set("customerPhone")} data-testid="edit-project-phone-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={f.customerEmail} onChange={set("customerEmail")} data-testid="edit-project-email-input" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Address</Label><Textarea rows={2} value={f.customerAddress} onChange={set("customerAddress")} data-testid="edit-project-address-input" /></div>
            <div className="space-y-1.5"><Label>Advance paid (₹)</Label><Input type="number" value={f.advancePaidAmount} onChange={set("advancePaidAmount")} data-testid="edit-project-advance-input" /></div>
            <div className="space-y-1.5"><Label>Payment reference</Label><Input value={f.paymentRefNumber} onChange={set("paymentRefNumber")} data-testid="edit-project-ref-input" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Customer store visit (clear if not visiting)</Label><Input type="datetime-local" value={f.customerVisitAt} onChange={set("customerVisitAt")} data-testid="edit-project-visit-input" /></div>
            <div className="col-span-2 text-xs text-muted-foreground">Balance due will be {inr(project.grandTotal - (+f.advancePaidAmount || 0))}</div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="edit-project-save">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const render = (tpl, p) => tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => ({
  client_name: p.customerName, project_code: p.projectCode, quote_code: p.quoteCode, tracking_info: trackingInfo(p.delivery),
  balance_due: inr(p.balanceDueAmount), grand_total: inr(p.grandTotal), visit_date: p.customerVisitAt ? fdt(p.customerVisitAt) : "",
}[k] ?? ""));

export function NotifyDialog({ project }) {
  const [open, setOpen] = useState(false);
  const { data } = useApi(open ? "/settings/templates" : null);
  const [tid, setTid] = useState(null);
  const t = data?.templates.find((x) => x.id === tid);
  const msg = t ? render(t.body, project) : "";
  const href = !t ? "#" : t.channel === "EMAIL"
    ? `mailto:${project.customerEmail || ""}?subject=${encodeURIComponent(render(t.subject || "", project))}&body=${encodeURIComponent(msg)}`
    : `https://api.whatsapp.com/send?phone=${waPhone(project.customerPhone)}&text=${encodeURIComponent(msg)}`;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="notify-customer-button"><Bell className="h-4 w-4 mr-2" />Notify</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="notify-dialog">
          <DialogHeader><DialogTitle>Notify {project.customerName}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-2">
            {data?.templates.map((x) => (
              <button key={x.id} onClick={() => setTid(x.id)} data-testid={`notify-template-${x.id}`}
                className={`text-left rounded-md border px-3 py-2 text-sm flex items-center gap-2 ${tid === x.id ? "border-primary bg-primary/5" : "hover:bg-secondary/40"}`}>
                {x.channel === "EMAIL" ? <Mail className="h-4 w-4 text-sky-500" /> : <MessageCircle className="h-4 w-4 text-emerald-500" />}{x.name}
              </button>
            ))}
          </div>
          {t && <div className="rounded-md bg-muted/50 p-3 text-sm whitespace-pre-wrap" data-testid="notify-preview">{msg}</div>}
          <DialogFooter><Button asChild disabled={!t}><a href={href} target="_blank" rel="noreferrer" data-testid="notify-send-button">Open in {t?.channel === "EMAIL" ? "Email" : "WhatsApp"}</a></Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
